import type { ComboboxOption } from '@/components/ui/combobox'
import type {
    CarrierLegacyUnitCandidate,
    CarrierLegacyUnitHit,
} from '@/lib/api/carrier-shipment-api'

import { m } from '@/lib/paraglide/messages'

/** Lower-case, accents off, đ as d: staff type "hai nam" for "Hải Nam". */
export function foldVietnamese(text: string): string {
    return text
        .toLowerCase()
        .replace(/đ/g, 'd')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
}

function matches(search: string, text: string): boolean {
    const words = foldVietnamese(search).split(/\s+/).filter(Boolean)
    const haystack = foldVietnamese(text)
    return words.every((word) => haystack.includes(word))
}

interface Input {
    candidates: CarrierLegacyUnitCandidate[]
    others: CarrierLegacyUnitCandidate[]
    /** Province-wide search results for `search`. */
    hits: CarrierLegacyUnitHit[]
    search: string
    /** The receiver's current ward; its own communes are already listed. */
    wardCode: string
    /** False when the receiver cannot be edited here: no row moves it. */
    canMoveWard: boolean
}

/** A commune that belongs to another current ward: picking it moves there. */
type Move = Pick<
    CarrierLegacyUnitHit,
    'ward_code' | 'ward_name' | 'vtp_ward_id'
>

const moveValue = (move: Move) => `${move.ward_code}:${move.vtp_ward_id}`

/**
 * The commune picker's rows: the ward's candidates, then the other communes of
 * its old districts, then communes of other wards found by name. A commune the
 * server places in exactly one other current ward is listed as that ward's, so
 * picking it moves the receiver there instead of booking it under a ward it no
 * longer belongs to; its value is `wardCode:vtpWardId` and `moveFor` resolves
 * it. A commune with no single ward stays a plain pick.
 */
export function legacyUnitOptions({
    candidates,
    others,
    hits,
    search,
    wardCode,
    canMoveWard,
}: Input) {
    const keep = (unit: CarrierLegacyUnitCandidate) =>
        !search.trim() ||
        matches(search, `${unit.ward_name} ${unit.district_name}`)
    const own = new Set(candidates.map((unit) => unit.vtp_ward_id))
    const elsewhere = canMoveWard
        ? hits.filter(
              (hit) => hit.ward_code !== wardCode && !own.has(hit.vtp_ward_id),
          )
        : []
    const found = new Set(elsewhere.map((hit) => hit.vtp_ward_id))
    const moves: Move[] = []
    const otherRows: ComboboxOption[] = []
    for (const unit of others.filter(keep)) {
        const target = unit.current_ward_code
        if (canMoveWard && target && target !== wardCode) {
            const move = {
                ward_code: target,
                ward_name: unit.current_ward_name ?? '',
                vtp_ward_id: unit.vtp_ward_id,
            }
            moves.push(move)
            otherRows.push({
                value: moveValue(move),
                label: m.carrier_legacy_search_hit({
                    commune: unit.ward_name,
                    district: unit.district_name,
                    ward: move.ward_name,
                }),
                triggerLabel: unit.ward_name,
                group: m.carrier_legacy_other_group(),
            })
        } else if (!(canMoveWard && found.has(unit.vtp_ward_id))) {
            // A search hit names the ward of a commune the district list
            // could not place; the hit row replaces it.
            otherRows.push({
                value: String(unit.vtp_ward_id),
                label: unit.ward_name,
                group: m.carrier_legacy_other_group(),
            })
        }
    }
    const listed = new Set(moves.map(moveValue))
    const hitRows = elsewhere
        .filter((hit) => !listed.has(moveValue(hit)))
        .map((hit) => {
            moves.push(hit)
            return {
                value: moveValue(hit),
                label: m.carrier_legacy_search_hit({
                    commune: hit.legacy_ward_name,
                    district: hit.legacy_district_name,
                    ward: hit.ward_name,
                }),
                triggerLabel: hit.legacy_ward_name,
                group: m.carrier_legacy_search_group(),
            }
        })
    const options: ComboboxOption[] = [
        ...candidates.filter(keep).map((unit) => ({
            value: String(unit.vtp_ward_id),
            label: unit.ward_name,
        })),
        ...otherRows,
        ...hitRows,
    ]
    const moveFor = (value: string) =>
        moves.find((move) => moveValue(move) === value)
    return { options, moveFor }
}
